/**
 * The share of the painter view's vertical exaggeration a city wall takes by the way its outer face
 * turns (STYLE §8.2). painterProjection.glsl mirrors exaggerationShare.
 *
 * The painter drew the walls tall where they turn their outer face to him, and drew the moat outside
 * the walls in full where it lies behind them, with the wall a low band in front of it. At the whole
 * exaggeration a wall on the far side of the city rises high enough to hide its moat from the painter
 * view, so it takes farShare of it there, and turns smoothly between the two as its outer face turns
 * from the painter: `facing` is the cosine between that face's direction and the direction to the
 * painter's eye, both horizontal.
 */

import type { Vec3 } from '../geo/types';
import { anchorOutward, anchorShare } from './anchorCode';

export const WALL_EXAGGERATION = {
  farShare: 0.2,
  facingFrom: -0.3,
  facingTo: 0.3,
} as const;

export interface WallExaggerationRule {
  readonly farShare: number;
  readonly facingFrom: number;
  readonly facingTo: number;
}

function smoothstep(e0: number, e1: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0)));
  return t * t * (3 - 2 * t);
}

/** The share for a vertex with exaggeration code `code` at world `position`, the painter's eye at
 *  world `eye`: the fixed share of a part that carries one (anchorCode.shareFootCode), and 1 for
 *  every vertex whose code carries neither that nor a facing. */
export function exaggerationShare(
  code: number,
  position: Vec3,
  eye: Vec3,
  rule: WallExaggerationRule = WALL_EXAGGERATION
): number {
  const fixed = anchorShare(code);
  if (fixed !== null) return fixed;
  const outward = anchorOutward(code);
  if (!outward) return 1;
  const dx = eye[0] - position[0];
  const dz = eye[2] - position[2];
  const facing = (outward[0] * dx + outward[1] * dz) / Math.hypot(dx, dz);
  const turned = smoothstep(rule.facingFrom, rule.facingTo, facing);
  return rule.farShare + (1 - rule.farShare) * turned;
}
